import { SORT_FILTER_UID } from 'src/constants/elementIds';
import type {
  Filter,
  FilterOption,
  FilterOptionValue,
  SelectedFilterOption,
  SortOption,
} from 'src/types';
import { getDefaultSelectedFilterValues } from './getDefaultSelectedFilterValues';
import { isSameFilterOption } from './isSameFilterOption';

const getSelectedCheckboxFilterOptions = (filter: Filter) => {
  const selectedCheckboxes = filter.options.reduce(
    (filterOptionsAcc: FilterOptionValue[], filterOption: FilterOption) =>
      filterOption.active ? filterOptionsAcc.concat([filterOption.value]) : filterOptionsAcc,
    [],
  );

  if (selectedCheckboxes.length > 0) {
    // Note: no filterOptionUid as checkboxes do not have these
    return [{ filterUid: filter.uid, filterOptionValue: selectedCheckboxes }];
  }

  return [];
};

const getSelectedRadioFilterOptions = (filter: Filter) => {
  const selectedRadio: FilterOption | undefined = filter.options.find(
    (filterOption: FilterOption) => filterOption.active,
  );

  // Note: no filterOptionUid as radiobuttons do not have these
  return selectedRadio ? [{ filterUid: filter.uid, filterOptionValue: selectedRadio.value }] : [];
};

const getSelectedRangeFilterOptions = (filter: Filter) => {
  const selectedRangeFilterOptions: SelectedFilterOption[] = filter.options.reduce(
    (selectedFilterOptionsAcc: SelectedFilterOption[], filterOption: FilterOption) => {
      if (filterOption.active) {
        return selectedFilterOptionsAcc.concat([
          {
            filterUid: filter.uid,
            filterOptionUid: filterOption.uid,
            filterOptionValue: filterOption.value as string | number,
          },
        ]);
      }
      return selectedFilterOptionsAcc;
    },
    [],
  );

  return selectedRangeFilterOptions;
};

const getSelectedTreeFilterOptions = (filter: Filter) => {
  const selectedIndex = filter.options.findIndex((filterOption) => filterOption.active);

  return selectedIndex !== -1
    ? [
        {
          filterUid: filter.uid,
          filterOptionValue: filter.options[selectedIndex].value,
          filterOptionDepth: selectedIndex,
        },
      ]
    : [];
};

interface GetSelectedFilterOptions {
  checkbox: typeof getSelectedCheckboxFilterOptions;
  radio: typeof getSelectedRadioFilterOptions;
  range: typeof getSelectedRangeFilterOptions;
  tree: typeof getSelectedTreeFilterOptions;
}

const getActiveSelectedFilterOptions = (filters: Filter[]) =>
  filters.reduce((acc: SelectedFilterOption[], filter: Filter) => {
    const getSelectedFilterOptions: GetSelectedFilterOptions = {
      checkbox: getSelectedCheckboxFilterOptions,
      radio: getSelectedRadioFilterOptions,
      range: getSelectedRangeFilterOptions,
      tree: getSelectedTreeFilterOptions,
    };

    const selectedFilterOptions = getSelectedFilterOptions[filter.type]?.(filter);
    return acc.concat(selectedFilterOptions || []);
  }, []);

// Combine the default selected filter options with the active selected filter options
// Where a filter option exists in both, the active should take precedent
export const getSelectedFilterOptions = (filters: Filter[]) =>
  getDefaultSelectedFilterValues(filters).reduce(
    (acc: SelectedFilterOption[], defaultSelectedFilterOption: SelectedFilterOption) => {
      if (
        !acc.find((activeSelectedFilterOption) =>
          isSameFilterOption(activeSelectedFilterOption, defaultSelectedFilterOption),
        )
      ) {
        return acc.concat([defaultSelectedFilterOption]);
      }

      return acc;
    },
    getActiveSelectedFilterOptions(filters),
  );

export const getSelectedFilterOptionsWithSortFilter = (
  filters: Filter[],
  sortingOptions: SortOption[],
) => {
  const activeSortOption = sortingOptions.find(({ active }) => active);

  return [
    ...getSelectedFilterOptions(filters),
    ...(activeSortOption
      ? [
          {
            filterUid: SORT_FILTER_UID,
            filterOptionValue: activeSortOption.id,
          },
        ]
      : []),
  ];
};

export const getFiltersWithSortFilter = (filters: Filter[], sortingOptions: SortOption[]) => {
  const sortFilter: Filter = {
    active: false,
    options: sortingOptions.map(({ id, title, active }) => ({
      uid: id,
      value: id,
      title,
      active,
      activeCount: null,
    })),
    expandedByDefault: false,
    title: 'Sort',
    type: 'radio',
    uid: SORT_FILTER_UID,
  };

  return [sortFilter, ...filters];
};
